import type { RouteObject } from 'react-router';

import { lazy, useMemo, useState, Suspense } from 'react';
import { Outlet, useMatches, useNavigate } from 'react-router';

import { paths } from 'src/routes/paths';
import { ProtectedRoute } from 'src/routes/ProtectedRoute';

import AppLayout from 'src/layouts/app';

import { AppTitleContext } from './app-title-context';

const ExampleAppPage = lazy(() => import('src/pages/example-app'));
const DigitalSkillPage = lazy(() => import('src/pages/digital-skill'));
const SkillsProfilePage = lazy(() => import('src/pages/skills-profile'));
const LeaderboardPage = lazy(() => import('src/pages/leaderboard'));
const CareerProfilePage = lazy(() => import('src/pages/career-profile'));
const CvPage = lazy(() => import('src/pages/cv'));
const AiUsagePolicyPage = lazy(() => import('src/pages/ai-usage-policy'));
const ExplorePage = lazy(() => import('src/pages/explore'));
const CertificatePage = lazy(() => import('src/pages/certificate'));
const CheckInPage = lazy(() => import('src/pages/check-in'));
const CheckInCallbackPage = lazy(() => import('src/pages/check-in/Callback'));

function AppShell() {
  const navigate = useNavigate();
  const matches = useMatches();
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const titleContext = useMemo(() => ({ setTitleOverride }), []);
  const handle = matches.at(-1)?.handle as
    | { title?: string; hideDesktopTitle?: boolean; alignContentToLeft?: boolean }
    | undefined;
  return (
    <AppTitleContext.Provider value={titleContext}>
      <AppLayout
        title={titleOverride ?? handle?.title ?? ''}
        hideDesktopTitle={handle?.hideDesktopTitle}
        alignContentToLeft={handle?.alignContentToLeft}
        onBack={() => navigate(-1)}
      >
        <Outlet />
      </AppLayout>
    </AppTitleContext.Provider>
  );
}

export const appRoutes: RouteObject[] = [
  {
    path: paths.checkInCallback,
    element: (
      <Suspense fallback={null}>
        <CheckInCallbackPage />
      </Suspense>
    ),
  },
  {
    element: <AppShell />,
    children: [
      {
        path: '/example/app',
        handle: { title: 'MARKETING' },
        element: (
          <Suspense fallback={null}>
            <ExampleAppPage />
          </Suspense>
        ),
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: paths.skillsProfile,
            handle: { title: 'Skills profile', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <SkillsProfilePage />
              </Suspense>
            ),
          },
          {
            path: paths.leaderboard,
            handle: { title: 'Leaderboard', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <LeaderboardPage />
              </Suspense>
            ),
          },
          {
            path: paths.digitalSkill,
            handle: { title: 'Digital skill', hideDesktopTitle: true, alignContentToLeft: true },
            element: (
              <Suspense fallback={null}>
                <DigitalSkillPage />
              </Suspense>
            ),
          },
          {
            path: paths.careerProfile,
            handle: { title: 'Career profile', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <CareerProfilePage />
              </Suspense>
            ),
          },
          {
            path: paths.cv,
            handle: { title: 'Convert to CV', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <CvPage />
              </Suspense>
            ),
          },
          {
            path: paths.aiUsagePolicy,
            handle: { title: 'Using AI tools safely', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <AiUsagePolicyPage />
              </Suspense>
            ),
          },
          {
            path: paths.explore,
            handle: { title: 'Explore volunteering ideas', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <ExplorePage />
              </Suspense>
            ),
          },
          {
            path: paths.certificate,
            handle: { title: 'CERTIFICATE', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <CertificatePage />
              </Suspense>
            ),
          },
          {
            path: paths.checkIn,
            handle: { title: 'CHECK IN', hideDesktopTitle: true },
            element: (
              <Suspense fallback={null}>
                <CheckInPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
];
